/**
 * The door scanner's offline store.
 *
 * Non-negotiable 5: the scanner works with no network, against a locally
 * cached guest list, and syncs on reconnect. Two volunteers on two devices
 * must not admit the same seat twice — this file cannot guarantee that on
 * its own and does not pretend to. It admits optimistically against the
 * cache and queues the scan; the server's unique constraint is the arbiter,
 * and the sync result tells the volunteer which of their admissions were in
 * fact another door's.
 *
 * `localStorage`, like tickets: a guest list for one programme is a few
 * hundred short rows, and a synchronous read means the scanner answers on
 * the frame the QR is decoded rather than after a promise.
 */
const LIST_KEY = "sangam.guestlist";
const QUEUE_KEY = "sangam.scanqueue";

export interface GuestTicket {
  ref: string;
  qr_payload: string;
  state: string;
  seats: string[];
}

export interface GuestList {
  program_id: number;
  venue: string;
  date_time: string;
  generated_at: string;
  tickets: GuestTicket[];
}

export interface QueuedScan {
  qr_payload: string;
  program_id: number;
  device_id: string;
  scanned_at: string;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* full or unavailable; the scanner falls back to online-only */
  }
}

export const saveGuestList = (list: GuestList) => write(LIST_KEY, list);
export const loadGuestList = (): GuestList | null => read<GuestList | null>(LIST_KEY, null);

export function queueScan(scan: QueuedScan): void {
  write(QUEUE_KEY, [...read<QueuedScan[]>(QUEUE_KEY, []), scan]);
}

export const loadQueue = (): QueuedScan[] => read<QueuedScan[]>(QUEUE_KEY, []);
export const clearQueue = () => write(QUEUE_KEY, []);

/**
 * A stable id for this device, so the server can tell two doors apart when
 * their queued scans collide. Generated once and kept; it identifies a
 * phone, not a person.
 */
export function deviceId(): string {
  const KEY = "sangam.device";
  let id = read<string | null>(KEY, null);
  if (!id) {
    id = `door-${Math.random().toString(36).slice(2, 10)}`;
    write(KEY, id);
  }
  return id;
}

export type LocalOutcome = "VALID" | "ALREADY" | "INVALID" | "NOT_PAID" | "UNKNOWN";

/**
 * Decide, with no network, what to tell the volunteer.
 *
 * `UNKNOWN` is deliberately distinct from `INVALID`. "This ticket is not on
 * my list" and "my list may be out of date" are different things to say to
 * someone standing at a door, and collapsing them either turns away a
 * legitimate member or waves through a forgery.
 */
export function checkLocally(
  qrPayload: string,
  list: GuestList | null,
  locallyAdmitted: Set<string>,
): { outcome: LocalOutcome; ticket: GuestTicket | null } {
  if (!list) return { outcome: "UNKNOWN", ticket: null };
  const ticket = list.tickets.find((t) => t.qr_payload === qrPayload) ?? null;
  if (!ticket) return { outcome: "UNKNOWN", ticket: null };
  if (ticket.state === "CHECKED_IN" || locallyAdmitted.has(ticket.ref)) {
    return { outcome: "ALREADY", ticket };
  }
  if (ticket.state !== "PAID") return { outcome: "NOT_PAID", ticket };
  return { outcome: "VALID", ticket };
}
